import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { AuthContainer, AuthContent, AuthMarketing, H2, Icon, LayoutSplit, LoaderSpin, Text } from 'components';
import { useSessionContext } from 'contexts/session';
import { theme, toRem } from 'design';
import { AuthService } from 'lib';
import { routeConstants } from 'routes';
import { browserHistory } from 'routes/Router';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { IStore } from 'store/types';
import { getCurrentUserRequest, User } from 'store/User';
import { IUser } from 'types';

const WarningWrapper = styled.div`
  margin-bottom: ${toRem(12)};

  svg {
    position: relative;
    width: ${toRem(55)};
    height: ${toRem(55)};
    fill: ${theme.color.warning.medium};
  }
`;

interface IParams {
  token: string;
}

const AuthRemoteSession: React.FC = () => {
  const dispatch = useDispatch();
  const { setSession, getSession } = useSessionContext();
  const { token } = useParams<IParams>();
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.GET_CURRENT_USER_REQUEST));
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    AuthService.storeToken(token); // Store the token
    dispatch(getCurrentUserRequest(onSuccess, onFail));
  }, [token]);

  const onFail = () => {
    AuthService.clearToken(); // Clear the token
    setShowError(true);
  };

  const onSuccess = (user: IUser) => {
    if (user.remoteSession) {
      setSession(getSession(token)); // Initialize session with token.
      browserHistory.push(routeConstants.private.rootPath);
    } else {
      // The user is not a remote session user
      // Do not log in and show error
      onFail();
    }
  };

  return (
    <LayoutSplit
      left={
        <AuthContainer>
          <AuthContent>
            {!showError && isLoading && <LoaderSpin />}
            {!isLoading && showError && (
              <>
                <WarningWrapper>
                  <Icon icon="Caution" />
                </WarningWrapper>
                <H2 fontWeight="medium">Unable to log into remote session</H2>
                <Text size="small">Please try again or contact support</Text>
              </>
            )}
          </AuthContent>
        </AuthContainer>
      }
      right={<AuthMarketing />}
    />
  );
};

export default AuthRemoteSession;
